import { BASE_URL } from '@/config';
import type { Order } from '@/models/settlements';
import dayjs from 'dayjs';
import type {
  ModelCancellationPolicy,
  ModelOrderAmendmentRequest,
  ModelOrderResponse,
  ModelOrderUpdateRequest,
  ModelPaymentPlanResponse,
  ModelPlanPayment,
} from '@layup/api-sdk';
import { NetworkError } from '../errors';
import type { Product } from '../models/orders';
import { amendOrder, updateOrder } from '../services/orders';
import { addMetric, createException, createWarning } from './monitoring.utils';

export const getBenefactor = (order: ModelOrderResponse) => {
  const { plans } = order;
  if (plans) {
    const { benefactor } = plans[0];
    if (benefactor) return benefactor;
  }
  return {};
};

export const getTotalPaid = (order: ModelOrderResponse) => {
  return getPayments(order)
    .filter((payment) => payment.paid)
    .reduce((total, payment) => total + (payment.amount ? payment.amount : 0), 0);
};

export const getPayments = (order: ModelOrderResponse): ModelPlanPayment[] => {
  const { plans } = order;
  return (
    plans?.reduce((allPayments: ModelPlanPayment[], plan) => {
      return plan.payments ? [...allPayments, ...plan.payments] : allPayments;
    }, []) || []
  );
};

export const getOutstanding = (order: ModelOrderResponse): number => {
  const totalPaid = getTotalPaid(order) as number;
  const totalDue = order.plans?.reduce((sum, plan: any) => sum + plan.amountDue + plan.depositDue, 0) || 0;
  return Math.max(0, totalDue - totalPaid);
};

export const calcPlanValue = (plans: ModelPaymentPlanResponse[]) => {
  return plans ? plans.reduce((sum, plan: any) => sum + plan.amountDue + plan.depositDue, 0) : 0;
};

export const checkAvailablePaymentMethodsInPriceList = (products: any[] | null) => {
  const paymentMethods = {
    capitecPay: false,
    creditCard: false,
    eft: false,
    debicheck: false,
    payAt: false,
    terminalCard: false,
  };

  // For old merchants without a price list, enable all payment methods
  if (products == null) {
    Object.keys(paymentMethods).forEach((key) => {
      paymentMethods[key as keyof typeof paymentMethods] = true;
    });
    return paymentMethods;
  }

  products.forEach((product) => {
    if (product.name.includes('paymentMethodCapitecPay')) {
      paymentMethods.capitecPay = true;
    }
    if (product.name.includes('paymentMethodPeachCard')) {
      paymentMethods.creditCard = true;
    }
    if (product.name.includes('paymentMethodPeachEft')) {
      paymentMethods.eft = true;
    }
    if (product.name.includes('paymentMethodDebicheck')) {
      paymentMethods.debicheck = true;
    }
    if (product.name.includes('paymentMethodPayat')) {
      paymentMethods.payAt = true;
    }
    if (product.name.includes('paymentMethodDashpay') || product.name.includes('paymentMethodNedbank')) {
      paymentMethods.terminalCard = true;
    }
  });

  return paymentMethods;
};

export const AddOrderProduct = async (orderId: string, product: Product) => {
  try {
    const params: ModelOrderAmendmentRequest = {
      orderId: orderId,
      amendmentType: 'PRODUCT:CREATE',
      products: [product],
    };
    await amendOrder(params);
    addMetric('orders-addProduct', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-addProduct', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-addProduct', { status: 'error', channel: 'merchant' });
    }
    throw error;
  }
};

export const DeleteOrderProduct = async (orderId: string, product: Product) => {
  try {
    const params: ModelOrderAmendmentRequest = {
      orderId: orderId,
      amendmentType: 'PRODUCT:DELETE',
      products: [product],
    };
    await amendOrder(params);
    addMetric('orders-deleteProduct', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-deleteProduct', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-deleteProduct', { status: 'error', channel: 'merchant' });
    }
    throw error;
  }
};

export const UpdateOrderProduct = async (orderId: string, product: Product) => {
  try {
    const params: ModelOrderAmendmentRequest = {
      orderId: orderId,
      amendmentType: 'PRODUCT:UPDATE',
      products: [product],
    };
    await amendOrder(params);
    addMetric('orders-updateProduct', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-updateProduct', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-updateProduct', { status: 'error', channel: 'merchant' });
    }
    throw error;
  }
};

export const UpdateOrderEndDate = async (orderId: string, endDate: string, travelDate: string) => {
  try {
    const params: ModelOrderAmendmentRequest = {
      orderId: orderId,
      amendmentType: 'ENDDATE:UPDATE',
      endDate: endDate,
      travelDate: travelDate,
    };
    await amendOrder(params);
    addMetric('orders-updateEndDate', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-updateEndDate', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-updateEndDate', { status: 'error', channel: 'merchant' });
    }
    throw error;
  }
};

export const getOrderStatusVariant = (status: string | undefined) => {
  let variant:
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'default'
    | 'warning'
    | 'brand'
    | 'success'
    | 'info'
    | 'error'
    | null
    | undefined = undefined;

  switch (status) {
    case 'PARTIAL':
      variant = 'outline';
      break;
    case 'PLACED':
      variant = 'info';
      break;
    case 'COMPLETED':
      variant = 'success';
      break;
    case 'CANCELLED':
    case 'CANCELLING':
      variant = 'error';
      break;
    case 'SUSPENDED':
    case 'EXPIRED':
      variant = 'warning';
      break;
    default:
      variant = undefined;
      break;
  }

  return variant;
};

export const calculateLastPaymentDate = (orderCreatedAt: Date | string | undefined, cancellationTerms: any): Date => {
  const lastIndex = cancellationTerms?.definition?.length! - 1;
  const daysToEnd = Math.abs(cancellationTerms?.definition![lastIndex].end!);
  const lastPaymentDate = dayjs(orderCreatedAt).add(daysToEnd, 'day').toDate();
  return lastPaymentDate;
};

export const updateSuspendedOrder = async (
  orderId: string,
  orderToUpdate: Order,
  cancellationTerms?: ModelCancellationPolicy | undefined,
) => {
  try {
    const params: ModelOrderUpdateRequest = {
      state: orderToUpdate.state,
      reference: orderToUpdate.reference,
      cancellationPolicy: cancellationTerms,
    };
    await updateOrder(orderId, params);
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('updateSuspendedOrder', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('updateSuspendedOrder', { status: 'error', channel: 'merchant' });
    }
    throw error;
  }
};

export const getOrderLink = (orderId: string, orderType: string) => {
  const type = orderType === 'REWARDS' ? 'boost-rewards' : 'lay-by';
  const link = new URL(`${BASE_URL}/shopper/${type}/${orderId}`);

  return link;
};
